<template>
  <v-card class="elevation-2 main-card modal-card">

    <!-- Digital Display -->
    <div class="display-panel">
      <div>
        <div class="display-label">Peso en báscula</div>
        <div class="display-weight-row">
          <span class="display-weight">{{ pesoBasculaDisplay.toLocaleString('es-MX') }}</span>
          <span class="display-unit">kg</span>
        </div>
      </div>
      <div class="display-right">
        <!-- Badge modo ENTRADA / SALIDA -->
        <v-chip :color="modoColor" text-color="white" class="badge-chip font-weight-medium mb-1" small>
          <span class="badge-dot" :style="{ background: modoColorDot }"></span>
          {{ modoTexto }}
        </v-chip>

        <!-- Badge conexión báscula -->
        <v-chip
          :color="basicula.conectado ? 'green' : 'red'"
          text-color="white"
          class="badge-chip font-weight-medium"
          small
        >
          <v-icon left x-small>{{ basicula.conectado ? 'wifi' : 'wifi_off' }}</v-icon>
          {{ basicula.conectado ? 'Báscula OK' : 'Sin báscula' }}
        </v-chip>
      </div>
    </div>

    <!-- Info Grid -->
    <div class="info-grid">
      <div class="info-cell" v-for="item in infoItems" :key="item.label">
        <div class="info-label">{{ item.label }}</div>
        <div class="info-val">{{ item.value }}</div>
      </div>
    </div>

    <!-- Weight Input -->
    <div class="inputs-section">
      <v-layout row wrap>
        <v-flex xs12 class="px-2 mb-3">
          <div class="field-label">
            <span class="field-dot" style="background:#1565C0"></span>
            {{ modoTexto === 'Entrada' ? 'Peso inicial (vehículo vacío)' : 'Peso final (vehículo cargado)' }}
          </div>

          <v-text-field
            v-model.number="weight"
            type="number"
            :step="0.01"
            solo
            flat
            hide-details
            class="weight-input"
            background-color="white"
            placeholder="Ingresa el peso en kg"
            autofocus
            @input="weightManualOverride = true"
          >
            <template v-slot:append>
              <v-btn
                icon
                small
                :disabled="!basicula.conectado || basicula.peso <= 0"
                :title="basicula.conectado ? 'Tomar peso de la báscula' : 'Báscula no conectada'"
                @click="tomarPesoBascula"
              >
                <v-icon small color="primary">input</v-icon>
              </v-btn>
            </template>
          </v-text-field>

          <div v-if="basicula.raw" class="caption grey--text mt-1 ml-1">
            Último dato báscula: <strong>{{ basicula.raw }}</strong>
            <span class="ml-2">{{ timestampFormateado }}</span>
          </div>
        </v-flex>
      </v-layout>
    </div>

    <div class="actions-bar">
      <v-btn flat class="action-btn-ghost" @click="limpiar">
        <v-icon left small>refresh</v-icon>
        Limpiar
      </v-btn>

      <v-spacer></v-spacer>

      <v-btn
        color="primary"
        depressed
        class="action-btn"
        :disabled="weight <= 0"
        @click="guardarPesada"
      >
        <v-icon left small>check_circle</v-icon>
        Guardar pesada
      </v-btn>
    </div>

  </v-card>
</template>

<script>
const BASCULA_URL      = 'http://localhost:8766/peso'
const POLLING_INTERVAL_FAST = 2000
const POLLING_INTERVAL_SLOW = 7000

export default {
  name: 'ScaleWeighing',

  props: {
    modo: {
      type: String,
      required: true,
      validator: (v) => ['ENTRADA', 'SALIDA'].includes(v)
    },
    infoInvitation: {
      type: Object,
      required: true
    },
    preFilledData: {
      type: Object,
      default: () => ({})
    },
    isVisible: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      weight:     0,
      weightManualOverride: false,
      scaleNumber: 'BASCULA-01',
      infoItems:  [],
      timer:      null,
      pollingTimer: null,
      pollingInFlight: false,

      // Estado de la báscula física
      basicula: {
        peso:      0,
        raw:       '',
        timestamp: null,
        conectado: false,
        error:     null
      }
    }
  },

  computed: {
    isEntrada() {
      return this.modo === 'ENTRADA'
    },
    modoTexto() {
      return this.isEntrada ? 'Entrada' : 'Salida'
    },
    modoColor() {
      return this.isEntrada ? '#1565C0' : '#F59E0B'
    },
    modoColorDot() {
      return this.isEntrada ? '#1E88E5' : '#D97706'
    },
    pesoBasculaDisplay() {
      const val = Number(this.basicula.peso)
      return Number.isFinite(val) && val > 0 ? val : 0
    },
    timestampFormateado() {
      return this.basicula.timestamp
        ? new Date(this.basicula.timestamp).toLocaleTimeString('es-MX')
        : ''
    }
  },
  watch: {
    isVisible() {
      this.actualizarEstadoPolling()
    }
  },
  created() {
     if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.actualizarEstadoPolling()
  },
  mounted() {
    this.buildInfoItems()
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.actualizarEstadoPolling()
  },
  methods: {
    puedeHacerPolling() {
      const documentoVisible = typeof document === 'undefined' || !document.hidden
      return this.isVisible && documentoVisible
    },

    actualizarEstadoPolling() {
      if (this.puedeHacerPolling()) {
        this.iniciarPolling()
        return
      }

      this.detenerPolling()
    },

    parsePesoDesdeRaw(raw) {
      if (!raw || typeof raw !== 'string') return null
      const match = raw.match(/-?[\d.,]+/)
      if (!match) return null

      // Normaliza formatos como 24,324.000 o 24324,000
      const normalized = match[0].replace(/,/g, '')
      const parsed = Number(normalized)
      return Number.isFinite(parsed) ? parsed : null
    },

    resolverPesoBascula(data) {
      const pesoNumerico = Number(data && data.peso)
      if (Number.isFinite(pesoNumerico)) {
        return pesoNumerico
      }

      const desdeRaw = this.parsePesoDesdeRaw(data && data.raw)
      if (desdeRaw !== null) {
        return desdeRaw
      }

      return 0
    },

    async consultarBascula() {
      if (this.pollingInFlight) return
      this.pollingInFlight = true

      try {
        const res = await fetch(BASCULA_URL, { signal: AbortSignal.timeout(800) })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json()
        const pesoNormalizado = this.resolverPesoBascula(data)
        const conectado = data.conectado !== null && data.conectado !== undefined
          ? Boolean(data.conectado)
          : Boolean(pesoNormalizado > 0 || (data.raw !== null && data.raw !== undefined && String(data.raw).trim() !== ''))

        this.basicula = {
          peso:      pesoNormalizado,
          raw:       data.raw !== null && data.raw !== undefined ? data.raw : '',
          timestamp: data.timestamp !== null && data.timestamp !== undefined ? data.timestamp : null,
          conectado,
          error:     data.error !== null && data.error !== undefined ? data.error : null
        }

        // Sincroniza el campo con la báscula, salvo que el usuario haya editado manualmente
        if (pesoNormalizado > 0 && !this.weightManualOverride) {
          this.weight = pesoNormalizado
        }
      } catch {
        // No se pudo alcanzar el servidor Python
        this.basicula.conectado = false
      } finally {
        this.pollingInFlight = false
        if (this.puedeHacerPolling()) {
          this.programarSiguienteLectura()
        }
      }
    },

    tomarPesoBascula() {
      if (this.basicula.conectado && this.basicula.peso > 0) {
        this.weight = this.basicula.peso
        this.weightManualOverride = false
      }
    },

    iniciarPolling() {
      if (!this.puedeHacerPolling()) return
      this.detenerPolling()
      this.consultarBascula()
    },

    obtenerIntervaloPolling() {
      if (typeof document !== 'undefined' && document.hidden) {
        return POLLING_INTERVAL_SLOW
      }

      return this.basicula.conectado ? POLLING_INTERVAL_FAST : POLLING_INTERVAL_SLOW
    },

    programarSiguienteLectura(delay) {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
      }

      const espera = typeof delay === 'number' ? delay : this.obtenerIntervaloPolling()
      this.pollingTimer = setTimeout(() => {
        this.consultarBascula()
      }, espera)
    },

    onVisibilityChange() {
      if (!this.puedeHacerPolling()) {
        this.detenerPolling()
        return
      }

      this.programarSiguienteLectura(250)
    },

    detenerPolling() {
      if (this.pollingTimer) {
        clearTimeout(this.pollingTimer)
        this.pollingTimer = null
      }
    },

    buildInfoItems() {
      const conductor = this.preFilledData.driver_name ||
        (this.infoInvitation.guest_first_name + ' ' + this.infoInvitation.guest_last_name) ||
        'N/A'
      const placas   = this.preFilledData.vehicle_plate   || this.infoInvitation.vehicle_plate   || 'N/A'
      const vehiculo = this.preFilledData.vehicle_type    || this.infoInvitation.transport_type  || 'N/A'

      this.infoItems = [
        { label: 'Movimiento',    value: this.modoTexto },
        { label: 'Placas',        value: placas },
        { label: 'Transportista', value: this.infoInvitation.client_name || 'N/A' },
        { label: 'Producto',      value: this.infoInvitation.observations || 'Carga' },
        { label: 'Conductor',     value: conductor },
        { label: 'Báscula',       value: this.scaleNumber }
      ]
    },

    guardarPesada() {
      if (this.weight > 0) {
        this.$emit('save', {
          weight_kg:   Number(this.weight),
          scaleNumber: this.scaleNumber,
          timestamp:   new Date().toISOString(),
          modo:        this.modo,
          isEntrada:   this.isEntrada
        })
      }
    },

    limpiar() {
      this.weight = 0
      this.weightManualOverride = false
    }
  },

  beforeDestroy() {
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', this.onVisibilityChange)
    }
    this.detenerPolling()
  }
}
</script>

<style scoped>
/* Layout */
.modal-card {
  border-radius: 8px !important;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-width: 900px;
  width: 100%;
}
.main-card {
  font-family: 'Roboto', sans-serif;
}

/* Header */
.header-bar {
  background: #1565C0;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}
.header-icon-wrap {
  width: 36px;
  height: 36px;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.header-title {
  font-size: 16px;
  font-weight: 500;
  color: white;
  line-height: 1.2;
}
.header-sub {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.65);
  margin-top: 2px;
}
.header-right {
  text-align: right;
}
.consec-label {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
}
.fecha-label {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

/* Digital Display */
.display-panel {
  background: #111827;
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.display-label {
  font-size: 10px;
  color: #6B7280;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.display-weight-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}
.display-weight {
  font-size: 3.5rem;
  font-family: 'Roboto Mono', 'Courier New', monospace;
  font-weight: 500;
  color: #34D399;
  line-height: 1;
}
.display-unit {
  font-size: 1.1rem;
  color: #6B7280;
}
.display-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 12px;
}
.badge-chip {
  font-size: 12px !important;
  letter-spacing: 0.4px;
}
.badge-dot {
  width: 7px;
  height: 7px;
  border-radius: 50%;
  display: inline-block;
  margin-right: 6px;
  flex-shrink: 0;
}
.mini-stats-row {
  display: flex;
  gap: 20px;
}
.mini-stat {
  text-align: center;
}
.mini-label {
  font-size: 10px;
  color: #6B7280;
  letter-spacing: 1px;
  text-transform: uppercase;
  margin-bottom: 2px;
}
.mini-val {
  font-size: 13px;
  font-weight: 500;
  color: #D1D5DB;
  font-family: 'Roboto Mono', monospace;
}

/* Info Grid */
.info-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  border-bottom: 1px solid #e0e0e0;
}
.info-cell {
  padding: 12px 16px;
  border-right: 1px solid #e0e0e0;
}
.info-cell:nth-child(3n) {
  border-right: none;
}
.info-label {
  font-size: 10px;
  color: #9E9E9E;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 3px;
}
.info-val {
  font-size: 13px;
  font-weight: 500;
  color: #212121;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Inputs */
.inputs-section {
  padding: 20px 12px 8px;
  background: #F5F5F5;
  border-bottom: 1px solid #e0e0e0;
}
.field-label {
  font-size: 11px;
  color: #757575;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
}
.field-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}
.weight-input >>> input {
  font-size: 18px !important;
  font-family: 'Roboto Mono', monospace !important;
  font-weight: 500 !important;
  text-align: right !important;
  color: #212121 !important;
}
.weight-input >>> .v-input__slot {
  border: 1px solid #E0E0E0 !important;
  border-radius: 8px !important;
}
.neto-input >>> input {
  color: #1565C0 !important;
}

/* Actions */
.actions-bar {
  padding: 14px 16px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  border-bottom: 1px solid #e0e0e0;
}
.action-btn {
  border-radius: 6px !important;
  text-transform: none !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0.3px !important;
}
.action-btn-ghost {
  border-radius: 6px !important;
  text-transform: none !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  color: #757575 !important;
}

/* Steps */
.steps-bar {
  padding: 10px 20px;
  display: flex;
  align-items: center;
  background: #FAFAFA;
}
.step-item {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 12px;
  color: #9E9E9E;
}
.step-active {
  color: #212121;
}
.step-done {
  color: #212121;
}
.step-circle {
  width: 22px;
  height: 22px;
  border-radius: 50%;
  border: 1px solid #E0E0E0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 500;
  flex-shrink: 0;
  color: #9E9E9E;
}
.step-active .step-circle {
  background: #1565C0;
  border-color: #1565C0;
  color: white;
}
.step-done .step-circle {
  background: #0F6E56;
  border-color: #0F6E56;
  color: white;
}
.step-text {
  white-space: nowrap;
}
.step-divider {
  flex: 1;
  height: 1px;
  background: #E0E0E0;
  margin: 0 10px;
  max-width: 48px;
}

/* Responsive */
@media (max-width: 600px) {
  .info-grid {
    grid-template-columns: repeat(2, 1fr);
  }
  .info-cell:nth-child(3n) {
    border-right: 1px solid #e0e0e0;
  }
  .info-cell:nth-child(2n) {
    border-right: none;
  }
  .display-panel {
    flex-direction: column;
    align-items: flex-start;
  }
  .display-right {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }
  .actions-bar {
    flex-direction: column;
    align-items: stretch;
  }
  .actions-bar .v-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>